import { Injectable, Signal, computed, effect, signal } from '@angular/core';

import { hasValue } from '@frontend/sports/common/core/utils/extended-types';

import { ErrorViewModel } from '../../validation/models/models';
import { ClientErrorTrackingService } from '../../validation/services/tracking/client-error-tracking.service';
import { QuickBetPickErrorViewModel } from '../components/pick/quick-bet-pick-error.component';

type QuickbetErrorContainer = {
    pickErrors: ErrorViewModel[];
    offerChange: boolean;
};

@Injectable()
export class QuickBetErrorTrackingService {
    private previouslyTrackedError: ErrorViewModel | undefined;
    private previousOfferChange = false;

    private errors: Signal<QuickbetErrorContainer> = computed(() => {
        const error = this.pickErrors();

        if (error && error.showErrors && error.errors) {
            return { offerChange: this.offerChanged(), pickErrors: error.errors.filter(hasValue) };
        }

        return { offerChange: this.offerChanged(), pickErrors: [] };
    });

    private pickErrors = signal<QuickBetPickErrorViewModel | undefined>(undefined);
    private offerChanged = signal<boolean>(false);

    constructor(private clientErrorTrackingService: ClientErrorTrackingService) {
        effect(
            () => {
                if (this.shouldTrackErrorChanges()) {
                    this.clientErrorTrackingService.trackQuickBetErrors(this.errors()!.pickErrors, this.errors().offerChange);
                    this.setPreviouslyTrackedValues();
                }
            },
            { allowSignalWrites: true },
        );
    }

    set pickError(errorModel: QuickBetPickErrorViewModel) {
        this.pickErrors.set(errorModel);
    }

    set offerChange(offerchanged: boolean) {
        this.offerChanged.set(offerchanged);
    }

    private shouldTrackErrorChanges(): boolean {
        if (!this.errors().pickErrors.length && !this.errors().offerChange) {
            //this is needed if error removed and after that the same error happens again track as separate occasion
            this.setPreviouslyTrackedValues();

            return false;
        }
        if (this.errors().offerChange !== this.previousOfferChange) {
            return true;
        }

        const currentTrackedError = this.clientErrorTrackingService.getFirstTrackableError(this.errors().pickErrors);

        if (!currentTrackedError) {
            return false;
        }

        return this.previouslyTrackedError
            ? currentTrackedError.error.type !== this.previouslyTrackedError.error.type ||
                  currentTrackedError.message !== this.previouslyTrackedError.message
            : true;
    }

    private setPreviouslyTrackedValues(): void {
        this.previouslyTrackedError = this.clientErrorTrackingService.getFirstTrackableError(this.errors().pickErrors);
        this.previousOfferChange = this.errors().offerChange;
    }
}
